import React, { useContext } from 'react'
import { Account } from '../types/types';
import { TR, TD, TH, Button } from './common';
import { AuthContext } from '../context/auth';


type LiveStream = {
  accountId?: string
  accountName?: string
  applicationName: string
  type: string
  streams: {
    streamName: string
    sourceIp: string
    isConnected: boolean
    ingestServer: {
      nodeId: string | null
      serviceId: string | null
      role: string | null
      capability: string | null
      publishIp: string
      adminIp: string
      apiPort: number
    }
  }[]
}

export const LiveItem = (props: {livestream: LiveStream}) => {
  const livestream = props.livestream
  return (
      <TR>
        <TD>{livestream.accountName + ' (' + livestream.accountId + ')'}</TD>
        <TD>{livestream.applicationName}</TD>
        <TD>{livestream.type}</TD>
        <TD>{livestream.streams.filter(s=>s.isConnected).length}</TD>
        <TD>{livestream.streams.filter(s=>!s.isConnected).length}</TD>
      </TR>
      )
}

const LIVE_INGEST_URL = 'https://api.dna.ip-only.net/api/v2/selfservice/live-ingest/accounts/'
const PAGE_SIZE = 50       // accounts fetched per "See more"
const MAX_CONCURRENT = 4   // accounts fetched in parallel within a page

// Runs worker over items with at most `limit` in flight; stops picking new items once aborted
async function runPool<T>(items: T[], limit: number, signal: AbortSignal, worker: (item: T) => Promise<void>) {
  let next = 0
  const runners = Array.from({length: Math.min(limit, items.length)}, async () => {
    while (!signal.aborted && next < items.length) {
      const item = items[next++]
      try { await worker(item) } catch (e) { if (!signal.aborted) console.warn(e) }
    }
  })
  await Promise.all(runners)
}

async function fetchAccountStreams(account: Account, token: string, signal: AbortSignal): Promise<LiveStream[]> {
  const headers = {Authorization: 'Bearer ' + token}

  const res = await fetch(LIVE_INGEST_URL + account.id + '/applications', {headers, signal})
  // A 401/403 here only means no live-ingest access on this account (external API), not an expired session — skip it
  if (!res.ok) return []

  const apps = (await res.json() as any[]).filter(app => app.type === 'liveorigin' || app.type === 'hub')
  const found: LiveStream[] = []
  for (const app of apps) {
    const r = await fetch(LIVE_INGEST_URL + account.id + '/applications/' + app.name + '/publishedstreams', {headers, signal})
    if (!r.ok) continue
    const liveStream = await r.json() as LiveStream
    if (liveStream.streams.length > 0) {
      found.push({...liveStream, accountId: account.id, accountName: account.name.String})
    }
  }
  return found
}

export const LiveList = (props: {accounts: Account[]}) => {

  const {authenticated} = useContext(AuthContext)

  const [livestreams, setLiveStreams] = React.useState<LiveStream[]>([])
  const [fetchedCount, setFetchedCount] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [refreshCount, setRefreshCount] = React.useState(0) // bumped by "Refresh" to re-check from the first page
  const controllerRef = React.useRef<AbortController | null>(null)

  // Stable key so a new array with the same accounts doesn't restart from the first page
  const accountKey = props.accounts.map(a => a.id).join(',')

  const loadPage = React.useCallback(async (accounts: Account[], start: number, controller: AbortController) => {
    const {signal} = controller
    const page = accounts.slice(start, start + PAGE_SIZE)

    setLoading(true)
    await runPool(page, MAX_CONCURRENT, signal, async (account) => {
      const found = await fetchAccountStreams(account, authenticated.token, signal)
      if (found.length && !signal.aborted) setLiveStreams(prev => [...prev, ...found])
    })
    if (signal.aborted) return

    setFetchedCount(start + page.length)
    setLoading(false)
  }, [authenticated.token])

  React.useEffect(()=>{
    const controller = new AbortController()
    controllerRef.current = controller
    setLiveStreams([])
    setFetchedCount(0)
    loadPage(props.accounts, 0, controller)
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey, loadPage, refreshCount])

  const seeMore = () => {
    if (controllerRef.current && !loading) loadPage(props.accounts, fetchedCount, controllerRef.current)
  }

  const total = props.accounts.length
  const hasMore = fetchedCount < total

  return <>
    {total > 0 ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px', justifyContent: "flex-end" }}>
        <span><b>Checked {fetchedCount} of {total} accounts</b></span>
        {hasMore || loading ? (
          <Button $primary={true} disabled={loading} onClick={seeMore}>
            {loading ? 'Loading…' : 'Load more'}
          </Button>
        ) : (
          <Button $primary={true} onClick={()=>setRefreshCount(c => c + 1)}>
            Refresh
          </Button>
        )}
      </div>
    ) : null}
    <table style={{borderSpacing: 0}}>
      <thead>
        <tr>
          <TH>Account ID</TH>
          <TH>Origin</TH>
          <TH>Origin Type</TH>
          <TH>Connected streams</TH>
          <TH>Disconnected streams</TH>
        </tr>
      </thead>
      <tbody style={{maxHeight: 'calc(1000px - 300px)'}}>
        {livestreams.map(stream=>(<LiveItem key={stream.accountId + stream.applicationName} livestream={stream} />))}
        {!loading && livestreams.length === 0 && fetchedCount > 0 ? (
          <TR><TD colSpan={5}>No live streams found in the checked accounts</TD></TR>
        ) : null}
      </tbody>
    </table>
  </>
}
